import { Client } from '@microsoft/microsoft-graph-client';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { graphPlannerScopes } from '../auth/msalConfig';

/**
 * Acquire a Microsoft Graph API access token via MSAL.
 * Falls back to popup if silent acquisition fails (e.g. consent required).
 */
export const getGraphToken = async (instance, accounts) => {
  if (!accounts || accounts.length === 0) {
    throw new Error('No authenticated user found');
  }

  try {
    const response = await instance.acquireTokenSilent({
      ...graphPlannerScopes,
      account: accounts[0],
    });
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      const response = await instance.acquireTokenPopup({
        ...graphPlannerScopes,
        account: accounts[0],
      });
      return response.accessToken;
    }
    throw error;
  }
};

/**
 * Create an authenticated Microsoft Graph SDK client.
 * The authProvider callback is invoked on each request to supply a fresh token.
 */
export const getGraphClient = (instance, accounts) => {
  return Client.init({
    authProvider: async (done) => {
      try {
        const token = await getGraphToken(instance, accounts);
        done(null, token);
      } catch (error) {
        done(error, null);
      }
    },
  });
};

/**
 * Lightweight wrapper for one-off Graph REST calls without the full SDK.
 */
export const graphApiCall = async (instance, accounts, endpoint, options = {}) => {
  const token = await getGraphToken(instance, accounts);
  const url = endpoint.startsWith('https://')
    ? endpoint
    : `https://graph.microsoft.com/v1.0${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const msg = body?.error?.message || `Graph API error: ${response.status}`;
    throw new Error(msg);
  }

  if (response.status === 204) return null;
  return response.json();
};
